import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Loader2, CheckCircle, XCircle, FileText, Lock, MapPin, RefreshCw, Mail } from 'lucide-react';
import axios from 'axios';
import { CGUModal } from '../components/CGUModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Activate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: authLogin } = useAuth();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  
  // Resend link state
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [captcha, setCaptcha] = useState({ sessionId: '', question: '' });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    adresse_rue: '',
    adresse_cp: '',
    adresse_ville: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // CGU state
  const [cguAccepted, setCguAccepted] = useState(false);
  const [showCGUModal, setShowCGUModal] = useState(false);
  const [cguVersion, setCguVersion] = useState('1.0');

  // Fetch captcha for resend form
  const fetchCaptcha = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/captcha`);
      setCaptcha({
        sessionId: response.data.session_id,
        question: response.data.question
      });
      setCaptchaAnswer('');
    } catch (error) {
      console.error('Failed to fetch captcha:', error);
    }
  };

  // Handle resend link request
  const handleResendLink = async (e) => {
    e.preventDefault();
    
    if (!resendEmail || !captchaAnswer) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setResendLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-activation-link`, {
        email: resendEmail,
        captcha_session_id: captcha.sessionId,
        captcha_answer: captchaAnswer
      });
      
      setResendSuccess(true);
      toast.success('Si un compte existe avec cet email, un nouveau lien a été envoyé');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(message);
      fetchCaptcha(); // Refresh captcha on error
    } finally {
      setResendLoading(false);
    }
  };

  // Check token validity on mount
  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setTokenError('Lien d\'activation invalide');
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/auth/check-activation-token?token=${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setUserInfo(response.data);
        }
      } catch (error) {
        setTokenError(error.response?.data?.detail || 'Lien d\'activation invalide ou expiré');
      } finally {
        setLoading(false);
      }
    };
    
    checkToken();
  }, [token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    if (!cguAccepted) {
      toast.error('Vous devez accepter les CGU');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/activate-account`, {
        token,
        password: formData.password,
        adresse_rue: formData.adresse_rue || null,
        adresse_cp: formData.adresse_cp || null,
        adresse_ville: formData.adresse_ville || null,
        cgu_accepted: true,
        cgu_version: cguVersion
      });
      
      toast.success('Compte activé avec succès !');
      
      // Login the user
      if (response.data.access_token) {
        authLogin(response.data.access_token, response.data.user);
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFDFC] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#2E4A3D] mx-auto mb-4" />
          <p className="text-stone-600">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    // Resend success state
    if (resendSuccess) {
      return (
        <div className="min-h-screen bg-[#FDFDFC] flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-serif font-bold text-[#2E4A3D] mb-4">
              Email envoyé !
            </h2>
            
            <p className="text-stone-600 mb-6">
              Si un compte en attente d'activation existe avec cet email, un nouveau lien d'activation vous a été envoyé.
              <br /><br />
              <span className="text-sm text-stone-500">Pensez à vérifier vos spams.</span>
            </p>
            
            <Button
              onClick={() => navigate('/login')}
              className="btn-primary w-full"
            >
              Retour à la connexion
            </Button>
          </div>
        </div>
      );
    }
    
    // Show resend form
    if (showResendForm) {
      return (
        <div className="min-h-screen bg-[#FDFDFC] flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <button
              onClick={() => setShowResendForm(false)}
              className="flex items-center text-stone-600 hover:text-stone-800 mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </button>
            
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#2E4A3D]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#2E4A3D]" />
              </div>
              <h2 className="text-2xl font-serif font-bold text-[#2E4A3D] mb-2">
                Nouveau lien d'activation
              </h2>
              <p className="text-stone-600">
                Entrez votre email pour recevoir un nouveau lien
              </p>
            </div>
            
            <form onSubmit={handleResendLink} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                  data-testid="resend-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="captcha">Vérification : {captcha.question}</Label>
                <Input
                  id="captcha"
                  type="text"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder="Votre réponse"
                  required
                  data-testid="resend-captcha-input"
                />
              </div>
              
              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={resendLoading}
                data-testid="resend-submit-btn"
              >
                {resendLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Envoyer un nouveau lien
                  </>
                )}
              </Button>
            </form>
            
            <p className="text-xs text-stone-500 text-center mt-4">
              Le nouveau lien sera valide 72 heures.
            </p>
          </div>
        </div>
      );
    }
    
    // Default error state
    return (
      <div className="min-h-screen bg-[#FDFDFC] flex items-center justify-center p-8">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          
          <h2 className="text-2xl font-serif font-bold text-[#2E4A3D] mb-4">
            Lien invalide ou expiré
          </h2>
          
          <p className="text-stone-600 mb-6">
            {tokenError}
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={() => {
                setShowResendForm(true);
                fetchCaptcha();
              }}
              className="btn-primary w-full"
              data-testid="request-new-link-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Demander un nouveau lien
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Retour à la connexion
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Valid token - show activation form
  return (
    <div className="min-h-screen bg-[#FDFDFC] flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2E4A3D]/90 to-[#2E4A3D]/70 z-10" />
        <img 
          src="https://images.unsplash.com/photo-1675879102007-287fcf09f2ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxob3JzZSUyMHJpZGVyJTIwcG9ydHJhaXQlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzY3MDQyNTkxfDA&ixlib=rb-4.1.0&q=85"
          alt="Cavalier"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-center p-12">
          <h1 className="text-4xl font-serif font-bold text-white mb-4">Bienvenue sur Equibook</h1>
          <p className="text-xl text-white/80 max-w-md">
            Finalisez la création de votre compte pour accéder à toutes les fonctionnalités
          </p>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <Link
            to="/login"
            className="flex items-center gap-2 text-stone-600 hover:text-[#2E4A3D] mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            Retour à la connexion
          </Link>
          
          {/* Success indicator */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800">Compte approuvé !</p>
                <p className="text-sm text-green-700">
                  Bienvenue {userInfo?.prenom}. Finalisez votre inscription ci-dessous.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-8">
            <h2 className="text-3xl font-serif font-bold text-[#2E4A3D]">Activez votre compte</h2>
            <p className="text-stone-600 mt-2">Définissez votre mot de passe et complétez votre profil</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Password section */}
            <div className="bg-stone-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2 text-stone-700 font-medium">
                <Lock className="w-5 h-5" />
                <span>Créer votre mot de passe</span>
              </div>
              
              <div>
                <Label htmlFor="password" className="form-label">Mot de passe *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Minimum 6 caractères"
                    className="input-field pr-10"
                    data-testid="activate-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword" className="form-label">Confirmer le mot de passe *</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirmez votre mot de passe"
                  className="input-field"
                  data-testid="activate-confirm-password"
                  required
                />
              </div>
            </div>
            
            {/* Address section */}
            <div className="bg-stone-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2 text-stone-700 font-medium">
                <MapPin className="w-5 h-5" />
                <span>Adresse postale (optionnel)</span>
              </div>
              
              <div>
                <Label htmlFor="adresse_rue" className="form-label">Rue</Label>
                <Input
                  id="adresse_rue"
                  name="adresse_rue"
                  value={formData.adresse_rue}
                  onChange={handleChange}
                  placeholder="123 rue de la Paix"
                  className="input-field"
                  data-testid="activate-rue"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="adresse_cp" className="form-label">Code postal</Label>
                  <Input
                    id="adresse_cp"
                    name="adresse_cp"
                    value={formData.adresse_cp}
                    onChange={handleChange}
                    placeholder="75001"
                    className="input-field"
                    data-testid="activate-cp"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="adresse_ville" className="form-label">Ville</Label>
                  <Input
                    id="adresse_ville"
                    name="adresse_ville"
                    value={formData.adresse_ville}
                    onChange={handleChange}
                    placeholder="Paris"
                    className="input-field"
                    data-testid="activate-ville"
                  />
                </div>
              </div>
            </div>
            
            {/* CGU Acceptance */}
            <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
              <Checkbox
                id="cgu-accept"
                checked={cguAccepted}
                onCheckedChange={(checked) => setCguAccepted(checked)}
                className="mt-0.5"
                data-testid="activate-cgu-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="cgu-accept" className="text-sm text-stone-700 cursor-pointer">
                  J'ai lu et j'accepte les{' '}
                  <button
                    type="button"
                    onClick={() => setShowCGUModal(true)}
                    className="text-[#2E4A3D] font-medium underline hover:no-underline inline-flex items-center gap-1"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Conditions Générales d'Utilisation
                  </button>
                  {' '}*
                </Label>
              </div>
            </div>
            
            <Button
              type="submit"
              className="btn-primary w-full py-3"
              disabled={submitting || !cguAccepted}
              data-testid="activate-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Activation en cours...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Activer mon compte
                </>
              )}
            </Button>
          </form>
          
          {/* CGU Modal */}
          <CGUModal
            isOpen={showCGUModal}
            onClose={() => setShowCGUModal(false)}
            onAccept={(version) => {
              setCguAccepted(true);
              setCguVersion(version);
              setShowCGUModal(false);
            }}
          />
        </div>
      </div>
    </div>
  );
};
